import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import React, { useEffect, useState, useCallback } from "react";
import Auth from "../Auth";

function DuplicatedStripeAccounts() {
  const confirmAction = useConfirmation();
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [unlinkingUid, setUnlinkingUid] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDuplicates = useCallback(async (forceRefresh = false) => {
    setStatus("loading");
    setError(null);
    try {
      const res = await Auth.fetchWithIdToken("/api/admin/duplicate-stripe-accounts", {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ forceRefresh })
      });
      setStatus(res.status);
      setResult(res.lastResult);
      if (res.lastResult && res.lastResult.finishedAt) {
        setLastUpdated(new Date(res.lastResult.finishedAt));
      }
    } catch (e) {
      setError(e.message || "Failed to fetch duplicates");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDuplicates(true); // Force refresh
    setRefreshing(false);
  };

  const handleUnlink = async (uid, group) => {
    await confirmAction({
      title: "Unlink Stripe account",
      message: "Are you sure you want to unlink this user from the Stripe account?",
      actionLabel: "Unlink",
      destructive: true
    }, async () => {
      setUnlinkingUid(uid);
      try {
        const res = await Auth.fetchWithIdToken(`/api/admin/unlink-stripe/${uid}`, {
          method: "POST"
        });
        if (res.success) {
          setError(null);
          // Remove the user from the local state for immediate feedback
          setResult(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              duplicates: prev.duplicates.map(g => g.stripeCustomerId === group.stripeCustomerId ? {
                ...g,
                subscriptions: g.subscriptions.filter(u => u.id !== uid)
              } : g).filter(g => g.subscriptions.length > 1) // Remove groups with only one subscription
            };
          });
        } else {
          setError(res.error || "Failed to unlink");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setUnlinkingUid(null);
      }
    });
  };

  const getStatusBadge = (status) => {
    const baseStyle = {
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "500",
      textTransform: "uppercase",
      letterSpacing: "0.5px"
    };

    switch (status) {
      case "running":
        return <span style={{ ...baseStyle, backgroundColor: "#FEF3C7", color: "#92400E" }}>Running</span>;
      case "completed":
      case "ready":
        return <span style={{ ...baseStyle, backgroundColor: "#D1FAE5", color: "#065F46" }}>Completed</span>;
      case "error":
        return <span style={{ ...baseStyle, backgroundColor: "#FEE2E2", color: "#991B1B" }}>Error</span>;
      case "loading":
        return <span style={{ ...baseStyle, backgroundColor: "#DBEAFE", color: "#1E40AF" }}>Loading</span>;
      default:
        return <span style={{ ...baseStyle, backgroundColor: "#F3F4F6", color: "#374151" }}>{status}</span>;
    }
  };

  return (
    <div style={{
      padding: "24px",
      maxWidth: "1200px",
      margin: "0 auto",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "32px",
        paddingBottom: "16px",
        borderBottom: "2px solid #E5E7EB"
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: "28px",
            fontWeight: "600",
            color: "#111827"
          }}>
            Duplicated Stripe Accounts
          </h1>
          {lastUpdated && (
            <div style={{
              fontSize: "14px",
              color: "#6B7280",
              marginTop: "4px"
            }}>
              Last refreshed: {lastUpdated.toLocaleString()}
              {status === "completed" && (() => {
                const hoursAgo = Math.floor((Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60));
                if (hoursAgo < 24) {
                  return ` (${hoursAgo}h ago)`;
                } else {
                  const daysAgo = Math.floor(hoursAgo / 24);
                  return ` (${daysAgo}d ago)`;
                }
              })()}
            </div>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || status === "loading"}
          style={{
            padding: "12px 24px",
            backgroundColor: refreshing || status === "loading" ? "#9CA3AF" : "#7C3AED",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: refreshing || status === "loading" ? "not-allowed" : "pointer",
            transition: "background-color 0.2s"
          }}
        >
          {refreshing || status === "loading" ? "Refreshing..." : "Force Refresh"}
        </button>
      </div>

      {/* Loading State */}
      {status === "loading" && !result && (
        <div style={{
          textAlign: "center",
          padding: "48px",
          backgroundColor: "#F9FAFB",
          borderRadius: "12px",
          border: "1px solid #E5E7EB"
        }}>
          <div style={{ fontSize: "16px", color: "#6B7280" }}>Loading duplicate accounts...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          padding: "16px",
          backgroundColor: "#FEF2F2",
          border: "1px solid #FECACA",
          borderRadius: "8px",
          color: "#DC2626",
          marginBottom: "24px"
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Info Card */}
      {!result && status !== "loading" && (
        <div style={{
          backgroundColor: "#F0F9FF",
          border: "1px solid #BAE6FD",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "24px"
        }}>
          <div style={{ fontSize: "14px", color: "#0369A1", lineHeight: "1.5" }}>
            <strong>ℹ️ How it works:</strong><br />
            • Data automatically refreshes once per day<br />
            • Use "Force Refresh" to manually trigger an immediate scan<br />
            • Large datasets may take several minutes to process
          </div>
        </div>
      )}

      {/* Summary Card */}
      {result && (
        <div style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "32px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
        }}>
          <h3 style={{
            margin: "0 0 16px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#111827"
          }}>
            Summary
          </h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px"
          }}>
            <div>
              <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Status
              </div>
              <div style={{ marginTop: "4px" }}>
                {getStatusBadge(status)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Total Subscriptions
              </div>
              <div style={{ marginTop: "4px", fontSize: "20px", fontWeight: "600", color: "#111827" }}>
                {result.summary?.totalSubscriptions || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                With Stripe ID
              </div>
              <div style={{ marginTop: "4px", fontSize: "20px", fontWeight: "600", color: "#111827" }}>
                {result.summary?.subscriptionsWithStripeId || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Duplicate Groups
              </div>
              <div style={{ marginTop: "4px", fontSize: "20px", fontWeight: "600", color: result.summary?.duplicateGroups > 0 ? "#DC2626" : "#059669" }}>
                {result.summary?.duplicateGroups || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Duplicate Subscriptions
              </div>
              <div style={{ marginTop: "4px", fontSize: "20px", fontWeight: "600", color: result.summary?.totalDuplicateSubscriptions > 0 ? "#DC2626" : "#059669" }}>
                {result.summary?.totalDuplicateSubscriptions || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicates List */}
      {result && (
        <>
          {result.duplicates && result.duplicates.length > 0 ? (
            <div>
              <h3 style={{
                margin: "0 0 24px 0",
                fontSize: "20px",
                fontWeight: "600",
                color: "#111827"
              }}>
                Duplicate Account Groups ({result.duplicates.length})
              </h3>
              {result.duplicates
                .sort((a, b) => {
                  // Sort groups with different emails first
                  const aHasDifferentEmails = new Set(a.subscriptions.map(s => s.email)).size > 1;
                  const bHasDifferentEmails = new Set(b.subscriptions.map(s => s.email)).size > 1;

                  if (aHasDifferentEmails && !bHasDifferentEmails) return -1;
                  if (!aHasDifferentEmails && bHasDifferentEmails) return 1;
                  return 0;
                })
                .map((group) => (
                <div key={group.stripeCustomerId} style={{
                  marginBottom: "32px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
                }}>
                  <div style={{
                    padding: "20px 24px",
                    backgroundColor: "#F9FAFB",
                    borderBottom: "1px solid #E5E7EB"
                  }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#111827"
                    }}>
                      Stripe Customer ID: <span style={{ fontFamily: "monospace", backgroundColor: "#F3F4F6", padding: "2px 6px", borderRadius: "4px" }}>{group.stripeCustomerId}</span>
                    </h4>
                    <div style={{ fontSize: "14px", color: "#6B7280", marginTop: "4px" }}>
                      {group.subscriptions.length} account(s) sharing this Stripe customer
                      {new Set(group.subscriptions.map(s => s.email)).size > 1 && (
                        <span style={{
                          marginLeft: "8px",
                          padding: "2px 6px",
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500"
                        }}>
                          Different Emails
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#F9FAFB" }}>
                          <th style={{
                            textAlign: "left",
                            padding: "12px 24px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Email
                          </th>
                          <th style={{
                            textAlign: "left",
                            padding: "12px 24px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            User ID
                          </th>
                          <th style={{
                            textAlign: "center",
                            padding: "12px 24px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.subscriptions.map((u, userIndex) => (
                          <tr key={u.id} style={{
                            borderBottom: userIndex < group.subscriptions.length - 1 ? "1px solid #F3F4F6" : "none"
                          }}>
                            <td style={{ padding: "16px 24px" }}>
                              <a
                                href={`/admin/user/${u.email}`}
                                style={{
                                  color: "#7C3AED",
                                  textDecoration: "none",
                                  fontWeight: "500"
                                }}
                                onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                                onMouseOut={(e) => e.target.style.textDecoration = "none"}
                              >
                                {u.email}
                              </a>
                            </td>
                            <td style={{
                              padding: "16px 24px",
                              fontFamily: "monospace",
                              fontSize: "13px",
                              color: "#6B7280"
                            }}>
                              {u.id}
                            </td>
                            <td style={{ padding: "16px 24px", textAlign: "center" }}>
                              {group.subscriptions.length > 1 ? (
                                <button
                                  disabled={unlinkingUid === u.id}
                                  onClick={() => handleUnlink(u.id, group)}
                                  style={{
                                    padding: "8px 16px",
                                    backgroundColor: unlinkingUid === u.id ? "#9CA3AF" : "#DC2626",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    cursor: unlinkingUid === u.id ? "not-allowed" : "pointer",
                                    transition: "background-color 0.2s"
                                  }}
                                >
                                  {unlinkingUid === u.id ? "Unlinking..." : "Unlink"}
                                </button>
                              ) : (
                                <span style={{
                                  fontSize: "12px",
                                  color: "#6B7280",
                                  fontStyle: "italic"
                                }}>
                                  Cannot unlink last account
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: "center",
              padding: "48px",
              backgroundColor: "#F0FDF4",
              border: "1px solid #BBF7D0",
              borderRadius: "12px"
            }}>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#059669", marginBottom: "8px" }}>
                🎉 No Duplicates Found!
              </div>
              <div style={{ fontSize: "14px", color: "#065F46" }}>
                All Stripe customer accounts are unique.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DuplicatedStripeAccounts;
